import { Checkbox, CheckboxProps } from 'antd';
import { ReactNode } from 'react';
import { Control, useController, FieldValues, Path } from 'react-hook-form';
import styled, { CSSProperties, css } from 'styled-components';

export type CheckboxFieldProps<T extends FieldValues> = CheckboxProps & {
  requiredIcon?: boolean;
  label?: string;
  name: Path<T>;
  control: Control<T>;
  directionLabel?: 'horizontal' | 'vertical';
  optionLabel: ReactNode;
  labelStyle?: CSSProperties;
};

interface StyledWrapperProps {
  directionLabel: 'horizontal' | 'vertical';
}

const StyledContainer = styled.div`
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--cl_primary);
    border-color: var(--cl_primary);
  }
`;

const StyledWrapper = styled.div`
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'horizontal' &&
    css`
      display: flex;
      align-items: center;
      gap: 8px;
    `}
`;

const StyledLabel = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--cl_gray900);
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'vertical' &&
    css`
      margin-bottom: 6px;
    `}
`;

const StyledErrorMessage = styled.div`
  color: var(--cl_error500);
  margin-top: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

export function CheckboxField<T extends FieldValues>({
  label,
  name,
  control,
  requiredIcon = false,
  directionLabel = 'vertical',
  labelStyle,
  optionLabel,
  onChange: externalOnChange,
  value: externalValue,
  ...rest
}: CheckboxFieldProps<T>) {
  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <StyledContainer>
      <StyledWrapper directionLabel={directionLabel}>
        {label && (
          <StyledLabel style={labelStyle} directionLabel={directionLabel}>
            {label}
          </StyledLabel>
        )}

        <Checkbox
          value={value}
          onChange={(event) => {
            onChange(event.target.checked);
            externalOnChange?.(event);
          }}
          ref={ref}
          {...rest}
        >
          {optionLabel}
        </Checkbox>
      </StyledWrapper>

      {error?.message && <StyledErrorMessage>{error?.message}</StyledErrorMessage>}
    </StyledContainer>
  );
}
